<template>
  <v-dialog
    v-model="internalValue"
    max-width="1200px"
    scrollable
    content-class="ds-dialog-chat"
    :fullscreen="$vuetify.breakpoint.smAndDown"
  >
    <v-card class="ds-dialog assistant-card">
      <!-- Header -->
      <v-card-title class="ds-dialog-header py-4">
        <v-icon color="white" class="mr-3">mdi-robot-happy-outline</v-icon>
        <span class="ds-dialog-title">salvIA</span>
        <v-spacer></v-spacer>
        <v-btn class="close-btn" icon color="white" aria-label="Fechar" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Conversa -->
      <v-card-text ref="scroller" class="assistant-body px-4 pt-6 pb-4">
        <div v-if="messages.length === 0" class="assistant-empty text-center py-8">
          <v-icon size="48" color="grey lighten-1">mdi-message-text-outline</v-icon>
          <p class="mt-3 mb-0 grey--text text--darken-1">
            Pergunte sobre o paciente ou peça para registrar um dado.
          </p>
          <p class="mb-0 grey--text">
            Ex.: "Quais medicamentos ele usa?" ou "Inclua o peso 82 kg".
          </p>
        </div>

        <div
          v-for="(msg, i) in messages"
          :key="i"
          class="msg-row"
          :class="msg.role === 'user' ? 'msg-row--user' : 'msg-row--assistant'"
        >
          <div class="msg-bubble" :class="`msg-bubble--${msg.role}`">
            <div v-if="msg.text" class="msg-text">{{ msg.text }}</div>

            <!-- Cards de proposta anexos à resposta -->
            <div v-if="msg.proposals && msg.proposals.length" class="mt-2">
              <ProposalCard
                v-for="p in msg.proposals"
                :key="p.id"
                :proposal="p"
                @update="onProposalUpdate(msg, $event)"
              />
            </div>
          </div>
        </div>

        <div v-if="loading || uploading" class="msg-row msg-row--assistant">
          <div class="msg-bubble msg-bubble--assistant">
            <v-progress-circular
              indeterminate
              size="18"
              width="2"
              color="primary"
            ></v-progress-circular>
            <span class="ml-2 grey--text">{{ activityLabel }}</span>
          </div>
        </div>
      </v-card-text>

      <!-- Input -->
      <v-card-actions class="ds-dialog-actions">
        <!-- Anexar PDF: menu escolhe o fluxo. Laudo -> exam_ingest (1 Exame).
             Documento/prontuário -> ingestão pelo agente (N propostas, item a item).
             Input único; o `uploadKind` roteia no change. Nada é gravado antes de aprovar. -->
        <input
          ref="fileInput"
          type="file"
          accept="application/pdf"
          class="d-none"
          @change="onFileSelected"
        />
        <v-menu offset-y top>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              :loading="uploading"
              :disabled="loading || uploading"
              :title="
                effectivePatientId
                  ? 'Anexar PDF (laudo ou documento)'
                  : 'Selecione um paciente para anexar'
              "
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-paperclip</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="pickFile('exam')">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-flask-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Laudo de exame</v-list-item-title>
            </v-list-item>
            <v-list-item @click="pickFile('document')">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-file-document-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Documento / prontuário</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-textarea
          v-model="draft"
          placeholder="Escreva sua pergunta…"
          rows="1"
          auto-grow
          dense
          hide-details
          outlined
          class="assistant-input ml-2"
          :disabled="loading || uploading"
          @keydown.enter.exact.prevent="send"
        ></v-textarea>
        <!-- salvia-821: ditar a pergunta em vez de digitar. Reusa o
             DictationButton da salvia-567 (ditado de prescricao) — o componente ja
             e generico: emite `transcript` com o texto final e cuida sozinho da
             captura, do indicador "ouvindo" e do preview ao vivo.
             Diferenca proposital em relacao a tela de prescricao: la o texto passa
             por um mixin que o estrutura em itens e abre um dialogo de revisao;
             aqui ele so cai no campo, "como se tivesse sido digitado", que e o que
             o card pede. Quem revisa e o proprio medico, antes de enviar. -->
        <DictationButton
          v-if="!loading && !uploading"
          class="ml-2"
          hint="Reconhecendo… pode falar sua pergunta."
          empty-text="Pode falar…"
          :numbered="false"
          @transcript="onDitado"
        />
        <v-btn
          icon
          color="primary"
          class="ml-2"
          :disabled="loading || uploading || !draft.trim()"
          @click="send"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  chat,
  proposeFromExamPdf,
  ingestDocument,
} from "@/services/assistantService";
import ProposalCard from "./ProposalCard.vue";
import DictationButton from "@/features/patients/prescriptions/components/DictationButton.vue";

// Tipo de extração do laudo. Segue o padrão do upload de exame já existente
// (Exams.vue), que também fixa este tipo; fácil de tornar selecionável depois.
const LAUDO_EXAM_TYPE = "extractor_sangue";

/**
 * Painel de chat da salvIA (Salvia Agêntico T2.6).
 *
 * Reutilizável: com `patientId` já trabalha nesse paciente; sem ele, o agente
 * pede/confirma o paciente. Fala com /api/assistant/chat (proxy Laravel ->
 * comunicacao). Respostas podem trazer PROPOSTAS pendentes, renderizadas como
 * cards com Aprovar/Editar/Rejeitar — nada é gravado até a aprovação.
 */
export default {
  name: "ClinicalAssistantPanel",
  components: { ProposalCard, DictationButton },
  props: {
    value: { type: Boolean, default: false },
    patientId: { type: [Number, String], default: null },
  },
  setup() {
    const { error } = useSnackbar();
    return { snackError: error };
  },
  data() {
    return {
      messages: [], // { role: 'user'|'assistant', text, proposals? }
      draft: "",
      loading: false,
      uploading: false, // envio/extração de PDF em andamento
      uploadKind: null, // 'exam' | 'document' — fluxo escolhido no menu do clipe
      // Tipo do upload EM ANDAMENTO. Diferente de uploadKind (zerado assim que o
      // arquivo é escolhido): este sobrevive durante a chamada, pra escolher a
      // mensagem do balão de atividade na conversa. Null quando não há upload.
      uploadingKind: null, // 'exam' | 'document'
      // Paciente resolvido pelo agente durante a conversa (atalho de
      // continuidade): uma vez identificado, é fixado e enviado nos próximos
      // turnos. Complementa o histórico. Null até o agente identificar alguém.
      focusPatientId: null,
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    // Paciente efetivo: o fixado na conversa tem prioridade; senão, o da ficha.
    effectivePatientId() {
      if (this.focusPatientId != null) return this.focusPatientId;
      return this.patientId != null ? Number(this.patientId) : null;
    },
    // Texto do balão de atividade da salvIA. Durante o upload, diferencia por
    // tipo (documento roda LLM e demora mais, então convém sinalizar o que faz);
    // fora do upload, é o "Pensando…" do chat.
    activityLabel() {
      if (this.uploading) {
        return this.uploadingKind === "exam"
          ? "Lendo o laudo…"
          : "Analisando o documento…";
      }
      return "Pensando…";
    },
  },
  watch: {
    // Trocar de paciente na navegação (abrir outra ficha) zera o foco fixado,
    // para não vazar o paciente anterior para a nova conversa.
    patientId() {
      this.focusPatientId = null;
    },
  },
  methods: {
    /**
     * Texto ditado cai no campo — salvia-821.
     *
     * CONCATENA em vez de substituir: o reconhecimento entrega em trechos (o
     * `transcript` dispara a cada pausa da fala), entao sobrescrever perderia tudo
     * o que veio antes. Tambem preserva o que o medico ja tinha digitado — ele pode
     * escrever metade e ditar o resto.
     *
     * Nao envia sozinho: o card pede que o texto entre "como se tivesse sido
     * digitado". Mandar direto tiraria a chance de corrigir o que o reconhecimento
     * entendeu errado, e aqui o texto vira acao clinica.
     */
    onDitado(trecho) {
      const texto = (trecho || "").trim();
      if (!texto) return;
      this.draft = this.draft ? `${this.draft.trim()} ${texto}` : texto;
    },

    close() {
      this.internalValue = false;
    },
    async send() {
      const pergunta = this.draft.trim();
      if (!pergunta || this.loading) return;

      // Histórico = turnos já na tela (antes desta pergunta). Limita aos últimos
      // 20 para não inflar o payload (o backend ainda corta os mais antigos).
      const historico = this.messages
        .filter((m) => m.text && (m.role === "user" || m.role === "assistant"))
        .slice(-20)
        .map((m) => ({ role: m.role, content: m.text }));

      this.messages.push({ role: "user", text: pergunta });
      this.draft = "";
      this.loading = true;
      this.scrollToBottom();

      try {
        const { reply, proposals, patientId } = await chat(
          pergunta,
          this.effectivePatientId,
          historico
        );
        // Fixa o paciente resolvido pelo agente para os próximos turnos.
        if (patientId != null) this.focusPatientId = Number(patientId);
        this.messages.push({
          role: "assistant",
          text: reply,
          proposals: proposals || [],
        });
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Não foi possível falar com a salvIA agora.";
        this.messages.push({ role: "assistant", text: msg, proposals: [] });
        this.snackError(msg);
      } finally {
        this.loading = false;
        this.scrollToBottom();
      }
    },
    onProposalUpdate(msg, updated) {
      const idx = (msg.proposals || []).findIndex((p) => p.id === updated.id);
      if (idx !== -1) this.$set(msg.proposals, idx, updated);
    },
    pickFile(kind) {
      // Precisa de paciente: os endpoints exigem patient_id, e ingerir no paciente
      // errado é um erro clínico. Sem paciente em foco, orienta e para.
      if (!this.effectivePatientId) {
        this.snackError("Selecione um paciente para anexar um arquivo.");
        return;
      }
      this.uploadKind = kind; // roteia o onFileSelected
      this.$refs.fileInput?.click();
    },
    async onFileSelected(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      const kind = this.uploadKind;
      // Limpa input e modo já: permite reenviar o MESMO arquivo depois (o change
      // não dispara de novo se o value não mudar).
      input.value = "";
      this.uploadKind = null;
      if (!file) return;

      if (file.type !== "application/pdf") {
        this.snackError("Selecione um arquivo PDF válido.");
        return;
      }

      // Ecoa o anexo como turno do médico, pra conversa ficar legível.
      const label = kind === "exam" ? "Laudo" : "Documento";
      this.messages.push({ role: "user", text: `📎 ${label} enviado: ${file.name}` });
      this.uploadingKind = kind; // escolhe a mensagem do balão de atividade
      this.uploading = true;
      this.scrollToBottom();

      try {
        if (kind === "exam") {
          const proposal = await proposeFromExamPdf(
            file,
            LAUDO_EXAM_TYPE,
            this.effectivePatientId
          );
          this.messages.push({
            role: "assistant",
            text: "Analisei o laudo. Revise os itens antes de importar:",
            proposals: proposal ? [proposal] : [],
          });
        } else {
          // Documento/prontuário: o agente propõe um item por dado encontrado.
          const { reply, proposals, patientId } = await ingestDocument(
            file,
            this.effectivePatientId
          );
          if (patientId != null) this.focusPatientId = Number(patientId);
          this.messages.push({
            role: "assistant",
            text: reply || "Analisei o documento. Revise as propostas:",
            proposals: proposals || [],
          });
        }
      } catch (e) {
        // 422 = PDF sem texto extraível; o backend manda a razão em `message`.
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Não foi possível processar o arquivo.";
        this.messages.push({ role: "assistant", text: msg, proposals: [] });
        this.snackError(msg);
      } finally {
        this.uploading = false;
        this.uploadingKind = null;
        this.scrollToBottom();
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scroller?.$el || this.$refs.scroller;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
/* salvia-848: sem max-height proprio — ver .rt-body. O fundo vem do
   padrao de dialogo (salvia-dialogs.css pinta o .v-card__text de
   --ds-surface); o #f7f8f8 que estava aqui era codigo morto, perdia na
   especificidade e nunca chegou a tela. */
.assistant-body {
  min-height: 320px;
  /* Precisa de !important: a regra do design system
     (.v-dialog > .v-card:not(.ds-confirm) > .v-card__text) tem 4 classes e
     vence um seletor scoped, que tem 2. Era por isso que o fundo antigo
     (#f7f8f8) nunca aparecia. */
  background: var(--ds-blue-100, #e6efea) !important;
}
.msg-row {
  display: flex;
  margin-bottom: 14px;
}
.msg-row--user {
  justify-content: flex-end;
}
.msg-row--assistant {
  justify-content: flex-start;
}
/* Balao e fundo sao os dois off-white: separa-los por luminancia nao
   funciona (branco sobre --ds-surface da ~1.2:1). Quem marca a borda do
   balao e o contorno + a sombra. */
.msg-bubble {
  max-width: 88%;
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 0.95rem;
  line-height: 1.45;
}
.msg-bubble--user {
  background: var(--ds-primary-deep, #2f7359);
  color: #fff;
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.22);
  border-bottom-right-radius: 4px;
}
.msg-bubble--assistant {
  background: var(--ds-surface-white, #fff);
  color: var(--ds-text-strong, #263238);
  border: 1px solid var(--ds-blue-200, #c2ddd3);
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.14);
  border-bottom-left-radius: 4px;
}
.msg-text {
  white-space: pre-wrap;
  word-break: break-word;
}
.assistant-input :deep(textarea) {
  max-height: 120px;
}
</style>
